import { z } from 'zod';
import { IdSchema, TimestampSchema } from './encoding.js';
/**
 * Notification contracts (ERD §8.4; FRD-CORE-025, CORE-027, CORE-029; FRD-INT-011, INT-034).
 *
 * Channels are values, not structures: a later channel (FRD-CORE-028) is a new value here.
 */
export const NOTIFICATION_CHANNELS = ['in_app', 'email'];
export const NotificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
export const NOTIFICATION_DELIVERY_STATUSES = ['pending', 'sent', 'failed', 'read'];
export const NotificationDeliveryStatusSchema = z.enum(NOTIFICATION_DELIVERY_STATUSES);
/** One of the caller's own in-system notifications (`GET /api/v1/notifications`). */
export const InboxNotificationSchema = z.object({
    id: IdSchema,
    eventType: z.string(),
    title: z.string(),
    body: z.string(),
    subjectRecordType: z.string(),
    subjectRecordId: IdSchema,
    /** The record type's display name, e.g. "Employee" — never the record's own content. */
    subjectLabel: z.string(),
    deliveryStatus: z.enum(['sent', 'read']),
    createdAt: TimestampSchema,
    readAt: TimestampSchema.nullable(),
});
export const UnreadCountSchema = z.object({ unread: z.number().int().min(0) });
export const ReadAllResultSchema = z.object({ marked: z.number().int().min(0) });
/** A declared notification event (`GET /api/v1/platform/notification-events`). */
export const NotificationEventSchema = z.object({
    eventType: z.string(),
    owningModule: z.string(),
    description: z.string(),
    parties: z.array(z.string()),
    placeholders: z.array(z.string()),
});
export const NotificationRuleSchema = z.object({
    id: IdSchema,
    eventType: z.string(),
    name: z.string(),
    parties: z.array(z.string()),
    roleId: IdSchema.nullable(),
    roleName: z.string().nullable(),
    channels: z.array(NotificationChannelSchema),
    subjectTemplate: z.string(),
    bodyTemplate: z.string(),
    status: z.enum(['active', 'inactive']),
    /** For `If-Match` on a change (SDD §7.3). */
    version: z.number().int(),
});
const RuleNameSchema = z.string().trim().min(1).max(100);
const PartiesSchema = z.array(z.string().min(1).max(100)).max(50);
const ChannelsSchema = z.array(NotificationChannelSchema).min(1, 'choose at least one channel');
const SubjectTemplateSchema = z.string().trim().min(1).max(200);
const BodyTemplateSchema = z.string().trim().min(1).max(4000);
/**
 * A rule answers one declared event: the parties must be the event's, the templates may use only
 * its placeholders, and it must name somebody — a party or a role (validated by the API).
 */
export const CreateNotificationRuleRequestSchema = z.strictObject({
    eventType: z.string().min(3).max(200),
    name: RuleNameSchema,
    parties: PartiesSchema,
    roleId: IdSchema.nullable().optional(),
    channels: ChannelsSchema,
    subjectTemplate: SubjectTemplateSchema,
    bodyTemplate: BodyTemplateSchema,
});
/** Any subset of the rule's settings, including activation (`status`). Requires `If-Match`. */
export const UpdateNotificationRuleRequestSchema = z
    .strictObject({
    eventType: z.string().min(3).max(200).optional(),
    name: RuleNameSchema.optional(),
    parties: PartiesSchema.optional(),
    roleId: IdSchema.nullable().optional(),
    channels: ChannelsSchema.optional(),
    subjectTemplate: SubjectTemplateSchema.optional(),
    bodyTemplate: BodyTemplateSchema.optional(),
    status: z.enum(['active', 'inactive']).optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'a change names at least one setting');
/**
 * One delivery in the administrators' log (`GET /api/v1/platform/notifications`), with its failure
 * reason (FRD-INT-011). The body is deliberately absent: an administrator sees that a delivery
 * happened, not another user's message.
 */
export const NotificationDeliverySchema = z.object({
    id: IdSchema,
    ruleId: IdSchema,
    eventType: z.string(),
    recipientUserId: IdSchema,
    recipientName: z.string(),
    channel: NotificationChannelSchema,
    title: z.string(),
    deliveryStatus: NotificationDeliveryStatusSchema,
    failureReason: z.string().nullable(),
    attempts: z.number().int(),
    subjectRecordType: z.string(),
    subjectRecordId: IdSchema,
    createdAt: TimestampSchema,
    sentAt: TimestampSchema.nullable(),
    readAt: TimestampSchema.nullable(),
});
//# sourceMappingURL=notifications.js.map